import React, { useCallback, useContext, useEffect, useState } from 'react';
import head from 'lodash/head';
import { useDispatch, useSelector } from 'react-redux';
import { AnniversaryLogo, ChevronLeft, ChevronRight, ExploreAll, Favorite, HeaderMapBalloon, } from '@yme/assets/icons';
import { AccordionItem } from '@yme/atoms/accordion';
import { GHOST_VARIANT, ICON_ONLY } from '@yme/atoms/button';
import { Container, Row } from '@yme/atoms/grid-old';
import { ICON_SIZE, SVGIcon } from '@yme/atoms/svg-icon';
import { PAGE_TYPE, PDP_TYPE, PLP_TYPE } from '@yme/common/constants';
import { Megamenu } from '@yme/components/Header/Megamenu';
import { Search } from '@yme/components/Header/Search';
import { useSearch } from '@yme/components/Header/Search/useSearch';
import { IconStyled, LinkStyled, LinkWrapperStyled } from '@yme/components/Header/styled';
import { Link } from '@yme/components/Link';
import { useOutsideClick, useWindowSize, useTouchOnly, useWishlist } from '@yme/hooks/index';
import { ButtonLink, ButtonLinkWrapper } from '@yme/molecules/buttons';
import { LanguageSelector } from '@yme/molecules/language-selector';
import { getDealerLocatorPageUrl, getIs70thLogoEnabled, getIsDriverLicenseEnabled, getLanguageRootPath, getRTL, } from '@yme/selectors/common';
import { useTranslation } from '@yme/services/i18n';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { EVENT_NAME_MAP } from '@yme/utils/constants';
import { getIs35KWIconShowed, getIsYAMTIconShowed } from '../Megamenu/helpers';
import { addOnClickHandler } from '../Menu/utils';
import { MenuUnitCard } from '../MenuUnitCard';
import { ANNIVERSARY_LOGO_LINK, EXTRA_MENU_TYPE, OUR_MODELS_TYPE } from '../constants';
import { setIsMenuActive, setPageType } from '../redux/actions';
import { getNavProducts, getPageType } from '../redux/selectors';
import { MenuContext } from './menuContext';
import { DealerLocatorWrapper, LanguageSelectorContainerStyled, MainMenuNavStyled, MobileMegamenuListItemStyled, MobileMegamenuListStyled, ScrollContainerStyled, UnitWrapperStyled, WishlistButtonWrapper, AnniversaryButtonStyled, } from './styled';
export function MobileMegamenu({ isMenuShowed, countries, labels, onClickMenu, isHeaderSticky, isSearchEnabled, }) {
    const { children, prevs, parentType, setMenuChildren, setPrevPages, setParentPageType, setNavButtonsContent, navButtonsContent, } = useContext(MenuContext);
    const dispatch = useDispatch();
    const isRightToLeft = useSelector(getRTL);
    const currentPageType = useSelector(getPageType);
    const navProducts = useSelector(getNavProducts);
    const isDriverLicenseEnabled = useSelector(getIsDriverLicenseEnabled);
    const languageRootPath = useSelector(getLanguageRootPath);
    const dealerLocatorPageUrl = useSelector(getDealerLocatorPageUrl);
    const is70thLogoEnabled = useSelector(getIs70thLogoEnabled);
    const [currentInRiverId, setCurrentInRiverId] = useState('');
    const [isFullWidth, setIsFullWidth] = useState(false);
    const [accordionActiveIndex, setAccordionActiveIndex] = useState(null);
    const { items: wishItems, isWishlistEnabled, isAuthorized } = useWishlist();
    const { isHeaderSearchOpen } = useSearch();
    const { isInfiniteDesktopView, isMobileView } = useWindowSize();
    const { t } = useTranslation();
    const currentChevronIcon = isRightToLeft ? ChevronLeft : ChevronRight;
    const isFirstLevel = prevs.length === 0;
    const items = addOnClickHandler(children?.items ?? []);
    const scrollPosition = isHeaderSticky ? 0 : window.scrollY;
    const PDPData = items.find(({ pageType }) => pageType === PDP_TYPE);
    const isTouchDevice = useTouchOnly();
    const likesLink = `${languageRootPath}/wishlist`;
    const hasWishItems = wishItems.length > 0;
    const anniversaryLogoLink = languageRootPath + ANNIVERSARY_LOGO_LINK;
    const { common_exploreAll_label: exploreAllLabel } = labels;
    const { elementRef } = useOutsideClick({
        eventName: EVENT_NAME_MAP.click,
        callback: () => null,
    });
    useEffect(() => {
        dispatch(setPageType(prevs?.slice()?.pop()?.pageType ?? ''));
    }, [dispatch, prevs]);
    useEffect(() => {
        if (isFirstLevel) {
            setParentPageType('');
        }
    }, [isFirstLevel, setParentPageType]);
    const onSetMegaMenuItems = (menuChildren, current, pageType, isImageFullWidth, inRiverId) => {
        setPrevPages(current);
        dispatch(setPageType(pageType));
        if (inRiverId) {
            setCurrentInRiverId(inRiverId);
        }
        if (pageType === PLP_TYPE) {
            setIsFullWidth(isImageFullWidth);
        }
        if (pageType === OUR_MODELS_TYPE) {
            setParentPageType(pageType);
        }
        if (pageType === EXTRA_MENU_TYPE) {
            setNavButtonsContent(EXTRA_MENU_TYPE, {
                title: current.title,
                link: current.link || '',
            });
        }
        if (pageType === PLP_TYPE) {
            const plpTitle = current.hasExtraMenuTitle
                ? current.title
                : `${exploreAllLabel} ${current.title}`;
            setNavButtonsContent(PLP_TYPE, {
                title: plpTitle,
                link: current.link || '',
            });
        }
        if (pageType === PAGE_TYPE) {
            setNavButtonsContent(PAGE_TYPE, {
                title: current.title,
                link: current.link || '',
            });
        }
        setMenuChildren(menuChildren);
    };
    const closeMenuHandler = useCallback(() => {
        dispatch(setIsMenuActive(false));
    }, [dispatch]);
    const onAccordionClick = useCallback((index) => {
        if (index !== accordionActiveIndex) {
            setAccordionActiveIndex(index);
        }
        else {
            setAccordionActiveIndex(null);
        }
    }, [accordionActiveIndex]);
    const MegaMenu = (React.createElement(React.Fragment, null,
        head(items)?.pageType !== PDP_TYPE ? (React.createElement(MobileMegamenuListStyled, { isRightToLeft: isRightToLeft },
            items.map((item, index) => {
                const { title, link, childPages, isListView, inRiverId = '', pageType, hasExtraMenuTitle, onClick, isImageFullWidth = false, } = item;
                if (pageType === PLP_TYPE && !navProducts[inRiverId]?.length) {
                    return null;
                }
                return (Boolean(childPages.length) &&
                    (isListView || parentType === OUR_MODELS_TYPE ? (React.createElement(MobileMegamenuListItemStyled, { isRightToLeft: isRightToLeft, key: title, role: "menuitem", isFirstLevel: isFirstLevel, onClick: () => {
                            onClick();
                            onSetMegaMenuItems({
                                title: children.title,
                                items: childPages,
                                pageType,
                                link,
                                hasExtraMenuTitle: children.hasExtraMenuTitle,
                            }, {
                                title,
                                link,
                                items,
                                pageType,
                                hasExtraMenuTitle,
                            }, pageType, isImageFullWidth, inRiverId);
                        } },
                        React.createElement("span", null, title),
                        React.createElement(SVGIcon, { type: currentChevronIcon }))) : (React.createElement(MobileMegamenuListItemStyled, { isRightToLeft: isRightToLeft, key: title, isFirstLevel: isFirstLevel, withAccordion: true },
                        React.createElement(AccordionItem, { title: title, isRightToLeft: isRightToLeft, isOpen: index === accordionActiveIndex, onClick: () => {
                                onClick();
                                onAccordionClick(index);
                            } },
                            React.createElement(Megamenu, { navItems: item, headerLabels: labels, closeMenuHandler: closeMenuHandler }))))));
            }),
            items.map(({ title, link, onClick, childPages }) => !childPages.length &&
                currentPageType !== PLP_TYPE && (React.createElement(MobileMegamenuListItemStyled, { isRightToLeft: isRightToLeft, key: title, isFirstLevel: isFirstLevel, isLink: true, role: "menuitem" },
                React.createElement("span", null,
                    React.createElement(Link, { to: link, onClick: () => {
                            onClick(link);
                            closeMenuHandler();
                        } }, title))))))) : null,
        currentPageType === PLP_TYPE ? (
        // TODO-Astrouski Grid old should be replaced after grid improvement
        React.createElement(ScrollContainerStyled, { isRightToLeft: isRightToLeft },
            React.createElement(Container, { lgPadding: "24px", smPadding: "16px", width: "100%" },
                React.createElement(Row, { lgMargin: "0 auto", smMargin: "0" }, navProducts[currentInRiverId]?.map((product) => {
                    const isYAMTIconShowed = getIsYAMTIconShowed(product);
                    const is35KWIconShowed = getIs35KWIconShowed(product);
                    return (React.createElement(UnitWrapperStyled, { key: product.key },
                        React.createElement(MenuUnitCard, { product: product, isYAMTIconShowed: isYAMTIconShowed, is35KWIconShowed: is35KWIconShowed, unitPath: PDPData?.link ?? '', isDriverLicenseEnabled: isDriverLicenseEnabled, label: labels.common_driverLicense_alt, isImageFullWidth: isFullWidth, closeMenuHandler: closeMenuHandler })));
                }))))) : null,
        is70thLogoEnabled && (React.createElement(FlexWrapper, { padding: "16px" },
            React.createElement(ButtonLinkWrapper, { buttonLink: anniversaryLogoLink },
                React.createElement(AnniversaryButtonStyled, { type: "button", isRightToLeft: isRightToLeft },
                    t('headerMenu_weCelebrateOur_label'),
                    React.createElement(SVGIcon, { type: AnniversaryLogo, size: ICON_SIZE.sm }))))),
        isFirstLevel && isMobileView && (React.createElement(LanguageSelectorContainerStyled, { isRightToLeft: isRightToLeft },
            React.createElement(LanguageSelector, { labels: labels, countries: countries, onSelectorClick: onClickMenu, isLight: true }),
            React.createElement(FlexWrapper, { gap: "16px" },
                Boolean(dealerLocatorPageUrl) && (React.createElement(DealerLocatorWrapper, null,
                    React.createElement(ButtonLink, { buttonLabel: "Dealer-locator", className: "menu-dealer-locator-button", buttonVariant: GHOST_VARIANT, hasIconButton: ICON_ONLY, buttonLink: dealerLocatorPageUrl, iconComponent: HeaderMapBalloon, onClick: closeMenuHandler }))),
                isWishlistEnabled && hasWishItems && isAuthorized && (React.createElement(WishlistButtonWrapper, { hasWishlistItems: hasWishItems },
                    React.createElement(ButtonLink, { buttonLabel: "Wishlist", className: "menu-wishlist-button", buttonVariant: GHOST_VARIANT, hasIconButton: ICON_ONLY, buttonLink: likesLink, iconComponent: Favorite, onClick: closeMenuHandler })))))),
        [EXTRA_MENU_TYPE, PLP_TYPE, PAGE_TYPE].includes(currentPageType) && (React.createElement(LinkWrapperStyled, { isInSubMenu: true, isTouchDevice: isTouchDevice, isRightToLeft: isRightToLeft },
            React.createElement(LinkStyled, { to: navButtonsContent[currentPageType].link, onClick: closeMenuHandler },
                React.createElement(IconStyled, { type: ExploreAll, size: ICON_SIZE.s }),
                navButtonsContent[currentPageType].title)))));
    if (isInfiniteDesktopView && !isTouchDevice) {
        return null;
    }
    return (React.createElement(MainMenuNavStyled, { isRightToLeft: isRightToLeft, ref: elementRef, isFirstMenu: isFirstLevel, isMenuShowed: isMenuShowed, scrollPosition: scrollPosition },
        isSearchEnabled && isFirstLevel && (React.createElement(Search, { isMobile: true, isRightToLeft: isRightToLeft })),
        !isHeaderSearchOpen && MegaMenu));
}
MobileMegamenu.displayName = 'MobileMegamenu';
